import {
  useEffect,
  useState
} from "react";
import {
  useNavigate,
  Link
} from "react-router-dom";
import {
  User,
  UserRound,
  Pin,
  LogOut,
  UserCircle,
  KeyRound,
  Receipt,
  Wallet,
  CreditCard,
  Bell,
  ShieldAlert,
  Loader2,
  ArrowRight,
  GraduationCap
} from "lucide-react";
import {
  useStudentAuth
} from "../../context/StudentAuthContext";
import {
  getStudentNotices
} from "../../api/student/studentDashboardApi";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const CATEGORY_VARIANTS = {
  GENERAL: "secondary",
  EXAM: "info",
  HOLIDAY: "danger",
  EVENT: "primary",
  FEE: "warning",
  URGENT: "danger",
};

const formatDate = (date) => {
  if (!date) return "—";
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const StudentHome = () => {
  const navigate = useNavigate();
  const { student, logout } = useStudentAuth();

  const [notices, setNotices] = useState([]);
  const [noticesLoading, setNoticesLoading] = useState(true);
  const toast = useToast();

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const data = await getStudentNotices();
        const noticeList = Array.isArray(data) ? data : data?.data || [];
        setNotices(noticeList.slice(0, 5));
      } catch (err) {
        toast.error("Could not load notices right now.");
      } finally {
        setNoticesLoading(false);
      }
    };

    fetchNotices();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/student-login");
  };

  if (!student) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-950 text-neutral-500 dark:text-neutral-400 gap-2">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-400 dark:text-neutral-500" />
        <span className="text-xs font-medium">Loading student portal...</span>
      </div>
    );
  }

  const fullName = [student.firstName, student.middleName, student.lastName]
    .filter(Boolean)
    .join(" ");

  const GenderIcon = student.gender === "Female" ? UserRound : User;

  return (
    <div className="min-h-screen bg-neutral-50/50 p-4 sm:p-6 lg:p-8 antialiased">
      <div className="max-w-5xl mx-auto space-y-6">
        
        {/* Welcome Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-neutral-900 border border-neutral-200/80 rounded-2xl p-6 shadow-xs">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">
              Student Dashboard
            </p>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
              {getGreeting()}, {student.firstName || "Student"}! 👋
            </h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
              Welcome back to your academic portal
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={student.status === "ENROLLED" ? "success" : "secondary"}>
              {student.status}
            </Badge>
          </div>
        </div>

        {/* Temporary Password Callout */}
        {student.passwordChanged === false && (
          <div className="bg-amber-50/90 border border-amber-200/80 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-100/80 text-amber-700 shrink-0">
                <ShieldAlert className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs font-bold text-amber-900">Security Recommendation</p>
                <p className="text-xs text-amber-800 font-medium mt-0.5">
                  Please change your temporary password for enhanced security.
                </p>
              </div>
            </div>
            <Link to="/student-panel/change-password" className="shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="bg-white dark:bg-neutral-900 border-amber-300 text-amber-900 hover:bg-amber-100 text-xs font-semibold"
              >
                Change Password
              </Button>
            </Link>
          </div>
        )}

        {/* Profile Card Summary */}
        <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl overflow-hidden">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-5">
              <div className="bg-sky-50 text-sky-600 p-4 rounded-2xl shrink-0 border border-sky-100 flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20">
                <GenderIcon className="h-8 w-8 sm:h-10 sm:w-10" />
              </div>

              <div className="flex-1 space-y-2">
                <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">{fullName}</h2>
                <div className="flex flex-wrap gap-2 text-xs font-medium">
                  <span className="bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 px-2.5 py-1 rounded-lg font-mono">
                    ID: {student.studentId}
                  </span>
                  <span className="bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 px-2.5 py-1 rounded-lg">
                    Class: {student.classId?.className}{" "}
                    {student.sectionId?.sectionName ? `- ${student.sectionId.sectionName}` : ""}
                  </span>
                  <span className="bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 px-2.5 py-1 rounded-lg">
                    Roll No: {student.rollNumber || "—"}
                  </span>
                  <span className="bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 px-2.5 py-1 rounded-lg">
                    Adm No: {student.admissionNo}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions & Menu Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Menu */}
          <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl">
            <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 py-3.5 px-5">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300">
                Quick Navigation
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5">
              <div className="grid grid-cols-3 gap-3">
                <Link
                  to="/student-panel/profile"
                  className="flex flex-col items-center justify-center p-4 bg-neutral-50/80 border border-neutral-100 dark:border-neutral-800 hover:bg-neutral-100/80 hover:border-neutral-200 rounded-xl text-center transition gap-2.5"
                >
                  <UserCircle className="h-6 w-6 text-sky-600" />
                  <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">My Profile</span>
                </Link>

                <Link
                  to="/student-panel/change-password"
                  className="flex flex-col items-center justify-center p-4 bg-neutral-50/80 border border-neutral-100 dark:border-neutral-800 hover:bg-neutral-100/80 hover:border-neutral-200 rounded-xl text-center transition gap-2.5"
                >
                  <KeyRound className="h-6 w-6 text-sky-600" />
                  <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">Security</span>
                </Link>

                <Link
                  to="/student-panel/results"
                  className="flex flex-col items-center justify-center p-4 bg-neutral-50/80 border border-neutral-100 dark:border-neutral-800 hover:bg-neutral-100/80 hover:border-neutral-200 rounded-xl text-center transition gap-2.5"
                >
                  <Receipt className="h-6 w-6 text-emerald-600" />
                  <span className="text-xs font-semibold text-neutral-700 dark:text-neutral-200">My Results</span>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* Fee Actions */}
          <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl">
            <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 py-3.5 px-5">
              <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300">
                Fee Services
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Link
                  to="/student-panel/fees/ledger"
                  className="flex items-center gap-3 bg-amber-50/60 border border-amber-200/60 hover:bg-amber-100/60 rounded-xl p-3.5 transition"
                >
                  <Wallet className="h-5 w-5 text-amber-600 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-amber-900">Fee Ledger</p>
                    <p className="text-[11px] text-amber-700 font-medium">Dues & balance</p>
                  </div>
                </Link>

                <Link
                  to="/student-panel/fees/receipts"
                  className="flex items-center gap-3 bg-sky-50/60 border border-sky-200/60 hover:bg-sky-100/60 rounded-xl p-3.5 transition"
                >
                  <Receipt className="h-5 w-5 text-sky-600 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-sky-900">Receipts</p>
                    <p className="text-[11px] text-sky-700 font-medium">Payment history</p>
                  </div>
                </Link>

                <Link
                  to="/student-panel/fees/pay"
                  className="flex items-center gap-3 bg-emerald-50/60 border border-emerald-200/60 hover:bg-emerald-100/60 rounded-xl p-3.5 transition"
                >
                  <CreditCard className="h-5 w-5 text-emerald-600 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-emerald-900">Pay Fees</p>
                    <p className="text-[11px] text-emerald-700 font-medium">Online payment</p>
                  </div>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Notices Section */}
        <Card className="border border-neutral-200/80 shadow-xs bg-white dark:bg-neutral-900 rounded-2xl">
          <CardHeader className="border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 py-3.5 px-5 flex flex-row items-center justify-between">
            <CardTitle className="text-xs font-bold uppercase tracking-wider text-neutral-600 dark:text-neutral-300 flex items-center gap-2">
              <Bell className="h-4 w-4 text-neutral-500 dark:text-neutral-400" />
              Notices & Announcements
            </CardTitle>
          </CardHeader>
          <CardContent className="p-5">
            

            {noticesLoading ? (
              <div className="flex items-center justify-center py-6 text-neutral-400 dark:text-neutral-500 gap-2 text-xs font-medium">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading notices...</span>
              </div>
            ) : notices.length === 0 ? (
              <p className="text-xs text-neutral-400 dark:text-neutral-500 italic text-center py-6">
                No notices published at this time.
              </p>
            ) : (
              <div className="space-y-3">
                {notices.map((notice) => (
                  <div
                    key={notice._id}
                    className="bg-neutral-50/80 border border-neutral-100 dark:border-neutral-800 rounded-xl p-4 space-y-1.5 hover:border-neutral-200 transition"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {notice.isPinned && (
                        <Pin className="h-3.5 w-3.5 text-sky-500 shrink-0" />
                      )}
                      <Badge variant={CATEGORY_VARIANTS[notice.category] || "secondary"}>
                        {notice.category}
                      </Badge>
                      <span className="text-[11px] font-medium text-neutral-400 dark:text-neutral-500 ml-auto">
                        {formatDate(notice.createdAt)}
                      </span>
                    </div>
                    <p className="font-semibold text-sm text-neutral-900 dark:text-neutral-100">{notice.title}</p>
                    <p className="text-xs text-neutral-600 dark:text-neutral-300 line-clamp-2 leading-relaxed">
                      {notice.content}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Logout Action */}
     
      </div>
    </div>
  );
};

export default StudentHome;